import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { ThemeProvider } from 'styled-components';
import { AuthProvider } from './context/AuthContext';
import { BannerProvider } from './context/BannerContext';
import { NoticeProvider } from './context/NoticeContext';
import { CartProvider } from './context/CartContext';
import { ProductProvider } from './context/ProductContext';
import { checkAppUpdate } from './services/appVersionService';
import { IoInformationCircleOutline } from 'react-icons/io5';
import GlobalStyle from './styles/GlobalStyle';
import theme from './styles/theme';

// Guards
import BackHandler from './components/BackHandler';
import RequireAuth from './components/guards/RequireAuth';
import RequirePhone from './components/guards/RequirePhone';
import RequireApproval from './components/guards/RequireApproval';

// System / Auth pages
import SplashPage from './pages/System/SplashPage';
import LinkPhonePage from './pages/Auth/LinkPhonePage';
import PendingPage from './pages/Auth/PendingPage';
import FindIdPage from './pages/Auth/FindIdPage';
import FindPasswordPage from './pages/Auth/FindPasswordPage';
import CompleteProfilePage from './pages/Auth/CompleteProfilePage';

// Login / SignUp
import LoginPage from './pages/Login/LoginPage';
import SignUpPage from './pages/Login/SignUpPage';
import AuthLoadingPage from './pages/Login/AuthLoadingPage';
import PhoneVerifyPage from './pages/Login/PhoneVerifyPage';

// User app
import MainLayout from './components/layout/MainLayout';
import DashboardPage from './pages/Home/DashboardPage';
import BannerDetailPage from './pages/Home/BannerDetailPage';
import SchedulePage from './pages/Schedule/SchedulePage';
import BoardPage from './pages/Board/BoardPage';
import ProductsPage from './pages/Products/ProductsPage';
import ProductDetailPage from './pages/Products/ProductDetailPage';
import OrderHistoryPage from './pages/Order/OrderHistoryPage';
import MyPage from './pages/Profile/MyPage';
import WorkHistoryPage from './pages/Profile/WorkHistoryPage';
import ChatPage from './pages/Chat/ChatPage';
import ChatDetailPage from './pages/Chat/ChatDetailPage';
import ChatMemoPage from './pages/Chat/ChatMemoPage';
import PartnersPage from './pages/Partners/PartnersPage';
import NotificationPage from './pages/Notification/NotificationPage';

// Policy
import PrivacyPolicyPage from './pages/Policy/PrivacyPolicyPage';
import TermsPage from './pages/Policy/TermsPage';
import LocationPolicyPage from './pages/Policy/LocationPolicyPage';

// Admin
import RequireAdmin from './components/guards/RequireAdmin';
import AdminLoginPage from './pages/Admin/AdminLoginPage';
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboardPage from './pages/Admin/Dashboard/AdminDashboardPage';
import AdminUsersPage from './pages/Admin/Users/AdminUsersPage';
import AdminProductsPage from './pages/Admin/Products/AdminProductsPage';
import AdminOrdersPage from './pages/Admin/Orders/AdminOrdersPage';
import AdminConstructionPage from './pages/Admin/Construction/AdminConstructionPage';
import AdminSchedulePage from './pages/Admin/Schedule/AdminSchedulePage';
import AdminNoticePage from './pages/Admin/Notice/AdminNoticePage';
import AdminUpdatesPage from './pages/Admin/Updates/AdminUpdatesPage';
import AdminSettingsPage from './pages/Admin/Settings/AdminSettingsPage';

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function VersionChecker() {
  const [toast, setToast] = useState('');
  useEffect(() => {
    checkAppUpdate({
      onUpdate: (content) => setToast(content),
    });
  }, []);
  if (!toast) return null;
  return (
    <UpdateToast>
      <IoInformationCircleOutline size={18} color="#4A6741" />
      <UpdateToastText>
        <UpdateToastLine1>새로운 버전으로 업데이트 중...</UpdateToastLine1>
        <UpdateToastLine2>{toast}</UpdateToastLine2>
      </UpdateToastText>
    </UpdateToast>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <AuthProvider>
        <BannerProvider>
        <NoticeProvider>
        <ProductProvider>
        <CartProvider>
        <Router>
          <ScrollToTop />
          <BackHandler />
          <VersionChecker />
          <Routes>
            {/* 스플래시 */}
            <Route path="/" element={<SplashPage />} />

            {/* 공개 라우트 */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignUpPage />} />
            <Route path="/auth-loading" element={<AuthLoadingPage />} />
            <Route path="/phone-verify" element={<PhoneVerifyPage />} />
            <Route path="/find-id" element={<FindIdPage />} />
            <Route path="/find-password" element={<FindPasswordPage />} />

            {/* RequireAuth만 */}
            <Route element={<RequireAuth />}>
              <Route path="/complete-profile" element={<CompleteProfilePage />} />
              <Route path="/link-phone" element={<LinkPhonePage />} />
              <Route path="/pending" element={<PendingPage />} />

              {/* RequireAuth + RequirePhone + RequireApproval */}
              <Route element={<RequirePhone />}>
                <Route element={<RequireApproval />}>
                  {/* MainLayout 밖 (헤더/풋터 없음) */}
                  <Route path="/products/:productId" element={<ProductDetailPage />} />
                  <Route path="/board" element={<BoardPage />} />
                  <Route path="/notifications" element={<NotificationPage />} />
                  <Route path="/chat" element={<ChatPage />} />

                  <Route element={<MainLayout />}>
                    <Route path="/home" element={<DashboardPage />} />
                    <Route path="/schedule" element={<SchedulePage />} />
                    <Route path="/products" element={<ProductsPage />} />
                    <Route path="/orders" element={<OrderHistoryPage />} />
                    <Route path="/mypage" element={<MyPage />} />
                    <Route path="/partners" element={<PartnersPage />} />
                  </Route>

                  {/* 채팅 상세 */}
                  <Route path="/chat/:roomId" element={<ChatDetailPage />} />
                  <Route path="/chat/:roomId/memo" element={<ChatMemoPage />} />

                  {/* 배너 상세 */}
                  <Route path="/banner/:bannerId" element={<BannerDetailPage />} />

                  {/* 시공내역 */}
                  <Route path="/work-history" element={<WorkHistoryPage />} />

                  {/* Policy 페이지 */}
                  <Route path="/policy/privacy" element={<PrivacyPolicyPage />} />
                  <Route path="/policy/terms" element={<TermsPage />} />
                  <Route path="/policy/location" element={<LocationPolicyPage />} />

                </Route>
              </Route>
            </Route>

            {/* Admin - 별도 인증 */}
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route element={<RequireAdmin />}>
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboardPage />} />
                <Route path="users" element={<AdminUsersPage />} />
                <Route path="products" element={<AdminProductsPage />} />
                <Route path="orders" element={<AdminOrdersPage />} />
                <Route path="construction" element={<AdminConstructionPage />} />
                <Route path="schedule" element={<AdminSchedulePage />} />
                <Route path="notice" element={<AdminNoticePage />} />
                <Route path="updates" element={<AdminUpdatesPage />} />
                <Route path="settings" element={<AdminSettingsPage />} />
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Router>
        </CartProvider>
        </ProductProvider>
        </NoticeProvider>
        </BannerProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;

const toastSlideUp = keyframes`
  from { transform: translateX(-50%) translateY(20px); opacity: 0; }
  to { transform: translateX(-50%) translateY(0); opacity: 1; }
`;
const UpdateToast = styled.div`
  position: fixed; bottom: 140px; left: 50%; transform: translateX(-50%);
  background: #fff; color: #1E2A32; padding: 14px 20px; border-radius: 12px;
  font-size: 14px; z-index: 9999; display: flex; align-items: center; gap: 10px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15); min-width: 270px; max-width: 340px;
  animation: ${toastSlideUp} 0.3s ease;
`;
const UpdateToastText = styled.div`flex: 1;`;
const UpdateToastLine1 = styled.div`font-size: 14px; font-weight: 500; color: #1E2A32;`;
const UpdateToastLine2 = styled.div`font-size: 12px; color: #8C9AA4; margin-top: 2px;`;
